/**
 * Request payload for processing a request for Uluro (print fulfillment)
 * Matches backend FpsApiV2.Api.Models.Requests.ProcessRequestForUluroRequest
 */
export interface ProcessRequestForUluroRequest {
  /** Required - ID of the print location */
  printLocationId: number;
  /** Required - Whether to print postage */
  printPostage: boolean;
  /** Optional - Printer type (max 50 chars) */
  printerType?: string;
  /** Optional - Sort order (max 50 chars) */
  sortOrder?: string;
  /** Required - ID of the user processing */
  processingUserId: number;
  /** Required - Username of modifier (max 255 chars) */
  modifiedBy: string;
}

/**
 * Result from processing a request for Uluro
 * Matches backend FpsApiV2.Api.Models.Requests.ProcessRequestForUluroResult
 */
export interface ProcessRequestForUluroResult {
  /** The request ID that was processed */
  requestId: number;
  /** Number of Uluro transactions created */
  transactionsCreated: number;
  /** Whether the request was sent to external vendor */
  isOutsourcing: boolean;
  /** New status ID assigned to the request */
  newRequestStatusId: number;
  /** New status ID assigned to files */
  newFileStatusId: number;
  /** Whether EEPay notification was sent */
  eePayNotificationSent: boolean;
}

/**
 * Error codes returned by the Process Request for Uluro API
 */
export enum ProcessRequestErrorCode {
  InvalidRequestStatus = 'InvalidRequestStatus',
  NoFiles = 'NoFiles',
  AlreadyProcessing = 'AlreadyProcessing',
  DuplicateEntry = 'DuplicateEntry',
  NotFound = 'NotFound',
}

/**
 * Error messages for each error code
 */
export const ProcessRequestErrorMessages: Record<
  ProcessRequestErrorCode,
  string
> = {
  [ProcessRequestErrorCode.InvalidRequestStatus]:
    'This request cannot be processed. It must be in Open status.',
  [ProcessRequestErrorCode.NoFiles]:
    'Cannot process request. No files are available.',
  [ProcessRequestErrorCode.AlreadyProcessing]:
    'This request is currently being processed by another user. Please try again later.',
  [ProcessRequestErrorCode.DuplicateEntry]:
    'These files were recently processed. Please wait 30 seconds before trying again.',
  [ProcessRequestErrorCode.NotFound]: 'Request not found.',
};

/**
 * Get a user-friendly error message for a given error code
 */
export function getProcessRequestErrorMessage(errorCode: string): string {
  return (
    ProcessRequestErrorMessages[errorCode as ProcessRequestErrorCode] ??
    'An error occurred while processing the request. Please try again.'
  );
}

/**
 * Print location option from API (when fetching available print locations)
 */
export interface PrintLocationOption {
  id: number;
  name: string;
  isOutsourcing: boolean;
}
